/**
 * Badge / Chip Component — Yoluno design system
 *
 * Pills (var(--radius-pill)), matching the .chip reference in yoluno-theme.css.
 * Default = brand-soft bg + brand-strong text (teal chip).
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  [
    'inline-flex items-center gap-[var(--space-2)]',
    'rounded-[var(--radius-pill)] border border-transparent',
    'px-[var(--space-4)] py-[var(--space-2)]',
    'text-caption font-body font-semibold',
    'transition-colors',
    'focus:outline-none focus:shadow-[var(--focus-ring)]',
  ].join(' '),
  {
    variants: {
      variant: {
        // Teal chip — primary / info
        default:
          'bg-[var(--brand-soft)] text-[var(--brand-strong)]',
        // Muted cream chip
        secondary:
          'bg-[var(--surface-sunken)] text-[var(--text-muted)] hover:bg-[var(--cream-300)]',
        // Danger chip
        destructive:
          'bg-[var(--danger-soft)] text-[var(--danger)]',
        // Outline only
        outline:
          'border-[var(--border-default)] text-[var(--text-body)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
